import { useForm, type DefaultValues, type FieldValues } from 'react-hook-form';
import type { z } from 'zod';

import { applyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import type { ChatSettings, UpdateChatSettingsRequest } from '@nucleus-commerce/api-client/admin';
import { ApiError, formFieldPath } from '@nucleus-commerce/api-client/transport';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useServerMessage } from './use-server-message';
import { useUpdateChatSettings } from './use-update-chat-settings';

/**
 * Backend paths are `body.<group>.<field>` (the chat settings PATCH body is the patch itself);
 * the tab forms are shaped by group, so dropping `body.` lands the error on the field. Errors
 * outside the tab's groups go to the form's alert instead of a field nobody sees.
 */
export function chatFieldPaths(error: unknown, groups: readonly string[]): Set<string> {
  const owned = new Set<string>();
  if (!(error instanceof ApiError)) return owned;
  for (const detail of error.details) {
    const path = formFieldPath(detail.field);
    if (groups.some((group) => path === group || path.startsWith(`${group}.`))) owned.add(path);
  }
  return owned;
}

/** What every form tab gets from the page: the store, its resolved settings, and edit access. */
export interface ChatSettingsTabProps {
  storeId: string;
  settings: ChatSettings;
  readOnly: boolean;
}

interface Options<TForm extends FieldValues> {
  storeId: string;
  schema: z.ZodType<TForm, TForm>;
  defaultValues: TForm;
  toPatch: (values: TForm) => UpdateChatSettingsRequest;
  /** The chat settings groups (and top-level keys) this tab's form owns. */
  groups: readonly string[];
  readOnly: boolean;
}

/**
 * One tab of the chat settings: a react-hook-form form registered with the save bar, saved through
 * the chat settings PATCH, with backend field errors on their fields and anything else (409 closed
 * store, 403) in the tab's alert.
 */
export function useChatSettingsForm<TForm extends FieldValues>({
  storeId,
  schema,
  defaultValues,
  toPatch,
  groups,
  readOnly,
}: Options<TForm>) {
  const update = useUpdateChatSettings(storeId);
  const serverMessage = useServerMessage();
  const form = useForm<TForm>({
    resolver: useI18nResolver(schema),
    defaultValues: defaultValues as DefaultValues<TForm>,
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync(toPatch(values));
      form.reset(values);
    } catch (error) {
      const knownFields = chatFieldPaths(error, groups);
      if (knownFields.size > 0 && applyFieldErrors(error, form.setError, { knownFields })) return;
      form.setError('root.server', { type: 'server', message: serverMessage(error) });
    }
  });
  useSaveBar({ form, onSave: submit, saving: update.isPending, disabled: readOnly });

  return { form, submit, saving: update.isPending };
}
